/*
 *
 * HandlersPanel
 *
 */

import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectGetSetCompositionSelected,
  makeSelectHandlesSelection,
} from './selectors';
import reducer from './reducer';
import { HandleUserSelection, RootState } from './types';
import {
  removeHandleDoorAction,
  selectionHandlesAction,
} from 'containers/HandlesPanel/actions';
import Panel from 'components/Panel';
import { PositionAngle, ShelfComposition } from 'lib/modelBuilder/constants';
import { makeSelectDrawerSelectedNumber } from 'containers/DrawersCompositionPanel/selectors';
import DrawersHandleSelection from 'containers/HandlesPanel/DrawersHandleSubPanel/DrawersHandleSelection';
import SelectionTypeHandlesContentPanel from 'containers/HandlesPanel/SelectionTypeHandlesContentPanel';
import { makeSelectShelvesSelectedLastCabinet } from 'containers/Configurator/selectors/selection';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import { makeSelectIsVisibleColorsPanel } from 'containers/Configurator/selectors/general';
import RemoveButton from 'components/RemoveButton';
import DoorHandlesSubPanel from 'containers/HandlesPanel/DoorHandlesSubPanel';
import DoorsHandlesSelection from 'containers/HandlesPanel/DoorHandlesSubPanel/DoorsHandlesSelection';
import DrawersHandleSubPanel from 'containers/HandlesPanel/DrawersHandleSubPanel';
import useMedia from 'use-media';
import MobileHandlesPanel from 'containers/HandlesPanel/MobileHandlesPanel';
import { ActivePanel } from '../Configurator/constants/general';
import PanelSwitcher from '../PanelSwitcher';
import { useTranslation } from 'react-i18next';
import {
  makeSelectIsHandlesMode,
  makeSelectPaintHandlesMode,
  makeSelectShouldForceChoiceHandles,
} from '../ModeBar/selectors';
import { ConfiguratorMode, HandlesMode } from '../ModeBar/reducer';
import { switchModeAction } from '../ModeBar/actions';
import StyledOverlay from '../../components/styles/StyledOverlay';
import PaintInstructions from '../../components/PaintInstructions';

interface StateProps {
  isVisible: boolean;
  handlePositionSelected: HandleUserSelection[];
  compositions: Set<ShelfComposition>;
  numberDrawers: number | undefined;
  selectedShelves: Shelf[];
  isPaintHandlesMode: boolean;
  paintHandlesMode: HandlesMode;
  displayPaintInstructions: boolean;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectIsVisibleColorsPanel(ActivePanel.handles),
  handlePositionSelected: makeSelectHandlesSelection(),
  compositions: makeSelectGetSetCompositionSelected(),
  numberDrawers: makeSelectDrawerSelectedNumber(),
  selectedShelves: makeSelectShelvesSelectedLastCabinet(),
  isPaintHandlesMode: makeSelectIsHandlesMode(),
  paintHandlesMode: makeSelectPaintHandlesMode(),
  displayPaintInstructions: makeSelectShouldForceChoiceHandles(),
});

function HandlesPanel() {
  useInjectReducer({ key: 'handlesPanel', reducer: reducer });
  const dispatch = useDispatch();
  const {
    isVisible,
    handlePositionSelected,
    compositions,
    numberDrawers,
    selectedShelves,
    isPaintHandlesMode,
    paintHandlesMode,
    displayPaintInstructions,
  } = useSelector(stateSelector);
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });

  const hidePanel = () => {
    dispatch(setColorsPanelAction());
    dispatch(switchModeAction(ConfiguratorMode.selector));
  };

  function handleSelection(positions: HandleUserSelection[]) {
    dispatch(selectionHandlesAction(positions));
  }

  let selectionComponent = null;

  function checkCanDelete() {
    for (const shelf of selectedShelves) {
      if (shelf.isBigShelf()) return false;
    }
    if (handlePositionSelected.length > 0) {
      if (handlePositionSelected.includes('Doors')) return true;
      for (const pos of Object.values(PositionAngle)) {
        if (handlePositionSelected.includes(pos)) return true;
      }
    }
    return false;
  }
  const canDelete = useMemo(() => checkCanDelete(), [checkCanDelete]);

  function shouldSelectDoor(): boolean {
    return (
      compositions.has(ShelfComposition.TwoDoors) ||
      compositions.has(ShelfComposition.FourDoors)
    );
  }

  function shouldSelectSlats() {
    return (
      compositions.has(ShelfComposition.LargeTwoDoors) ||
      compositions.has(ShelfComposition.LargeFourDoors)
    );
  }

  function shouldSelectDrawers() {
    return compositions.has(ShelfComposition.Drawer);
  }

  function removeHandles() {
    dispatch(removeHandleDoorAction());
    if (isMobile) {
      hidePanel();
    }
  }

  function selectAllDoorsHandle() {
    handleSelection([
      PositionAngle.FrontRight,
      PositionAngle.FrontLeft,
      PositionAngle.BackLeft,
      PositionAngle.BackRight,
    ]);
    dispatch(setColorsPanelAction(ActivePanel.handles, false));
  }

  function selection(e: any, position: HandleUserSelection, multi = true) {
    if (isMobile) {
      handleSelection([position]);
      dispatch(setColorsPanelAction(ActivePanel.handles, false));
    } else {
      if (handlePositionSelected.includes(position)) {
        if (e.shiftKey && multi && handlePositionSelected.length > 1) {
          // Unselect only if we have more than 2 element in selection
          handleSelection(handlePositionSelected.filter(e => e !== position));
        } else {
          handleSelection([position]);
        }
      } else {
        if (e.shiftKey && multi) {
          handleSelection([...handlePositionSelected, position]);
        } else {
          handleSelection([position]);
        }
      }
    }
  }

  if (compositions.size === 1) {
    if (
      compositions.has(ShelfComposition.TwoDoors) ||
      compositions.has(ShelfComposition.LargeTwoDoors)
    ) {
      selectionComponent = (
        <DoorsHandlesSelection
          handlePositionSelected={handlePositionSelected}
          selection={selection}
          compositions={compositions}
          handleSelection={handleSelection}
          doorPositions={[PositionAngle.FrontLeft, PositionAngle.FrontRight]}
          largeDoors={compositions.has(ShelfComposition.LargeTwoDoors)}
          selectedShelves={selectedShelves}
          allSelector={isMobile}
          selectAll={selectAllDoorsHandle}
        />
      );
    } else if (
      compositions.has(ShelfComposition.FourDoors) ||
      compositions.has(ShelfComposition.LargeFourDoors)
    ) {
      selectionComponent = (
        <DoorsHandlesSelection
          handlePositionSelected={handlePositionSelected}
          selection={selection}
          compositions={compositions}
          handleSelection={handleSelection}
          doorPositions={[
            PositionAngle.FrontLeft,
            PositionAngle.FrontRight,
            PositionAngle.BackLeft,
            PositionAngle.BackRight,
          ]}
          largeDoors={compositions.has(ShelfComposition.LargeFourDoors)}
          selectedShelves={selectedShelves}
          allSelector={isMobile}
          selectAll={selectAllDoorsHandle}
        />
      );
    } else if (compositions.has(ShelfComposition.Drawer) && numberDrawers) {
      selectionComponent = (
        <DrawersHandleSelection
          handlePositionSelected={handlePositionSelected}
          selection={selection}
          compositions={compositions}
          handleSelection={handleSelection}
          drawerPosition={Array.from(Array(numberDrawers).keys())}
        />
      );
    }
  } else {
    selectionComponent = (
      <SelectionTypeHandlesContentPanel
        handlePositionSelected={handlePositionSelected}
        selection={selection}
        handleSelection={handleSelection}
        doors={shouldSelectDoor()}
        drawers={shouldSelectDrawers()}
        slats={shouldSelectSlats()}
      />
    );
  }

  if (isMobile) {
    return (
      <div>
        <div className="flex w-full justify-center items-center">
          <div className="flex justify-centerpy-2">
            <div className="flex flex-col justify-center">
              {selectionComponent}
            </div>
          </div>
        </div>
        <MobileHandlesPanel
          handlePositionSelected={handlePositionSelected}
          compositions={compositions}
          remove={canDelete && removeHandles}
        />
      </div>
    );
  } else {
    return (
      <>
        <Panel
          close={hidePanel}
          isVisible={isVisible}
          autoclose={false}
          className={`${isPaintHandlesMode ? ' z-50 ' : ''}`}
        >
          <div className="flex w-full">
            {!isPaintHandlesMode && (
              <div className="lg:w-1/4 flex justify-center py-2">
                <div className="flex flex-col justify-center -mt-2">
                  <PanelSwitcher />
                  <div className="lg:mt">{selectionComponent}</div>
                </div>
                {canDelete && (
                  <div className="lg:mt-10 lg:ml-4">
                    <RemoveButton
                      onClick={() => {
                        dispatch(removeHandleDoorAction());
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {isPaintHandlesMode ? (
              <div className="flex w-full justify-center ">
                {paintHandlesMode === HandlesMode.doors && (
                  <DoorHandlesSubPanel
                    handlePositionSelected={handlePositionSelected}
                    bigShelves={false}
                    paint
                  />
                )}
                {paintHandlesMode === HandlesMode.bigShelves && (
                  <DoorHandlesSubPanel
                    handlePositionSelected={handlePositionSelected}
                    bigShelves
                    paint
                  />
                )}
                {paintHandlesMode === HandlesMode.drawers && (
                  <DrawersHandleSubPanel
                    handlePositionSelected={handlePositionSelected}
                  />
                )}
                {isPaintHandlesMode && displayPaintInstructions && (
                  <PaintInstructions />
                )}
              </div>
            ) : (
              <div className="flex lg:w-3/4 ">
                <DoorHandlesSubPanel
                  handlePositionSelected={handlePositionSelected}
                  bigShelves={
                    compositions.size === 1 &&
                    (compositions.has(ShelfComposition.LargeTwoDoors) ||
                      compositions.has(ShelfComposition.LargeFourDoors))
                  }
                />
                <DrawersHandleSubPanel
                  handlePositionSelected={handlePositionSelected}
                />
              </div>
            )}
          </div>
        </Panel>
        {isPaintHandlesMode && displayPaintInstructions && <StyledOverlay />}
      </>
    );
  }
}

export default HandlesPanel;
